import { pontosMontagem, type PontoMontagem } from '../../dominio/didatico/montagemEstabilizacao'
import { LigacaoCatraca3D } from './LigacaoCatraca3D'
import { Line } from '@react-three/drei'
import { useThree, type ThreeEvent, type RootState } from '@react-three/fiber'
import { useEffect, useMemo, useRef, useState } from 'react'
import * as THREE from 'three'
import {
  encontrarZonaDeSnap,
  equipamentoCompativelComZona,
  vetoresDeInstabilidade,
  zonasDaEstabilizacao,
  type AvaliacaoEstabilizacao,
  type ColocacaoEquipamento,
  type EquipamentoEstabilizacao,
  type PontoSolo,
  type PosicaoEstabilizacao,
  type ZonaEstabilizacao,
} from '../../dominio/didatico/estabilizacao'
import { postoDoVeiculo } from '../../dominio/didatico/configuracaoVeiculo'
import type { PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import { ADULTO_MASCULINO } from '../../dominio/didatico/perfisVitima'
import { POSE_SENTADA } from '../../dominio/didatico/rigVitima'
import {
  calcularDeslocamentoVerticalNoPonto,
  calcularPoseComSuspensao,
  ESTADO_SUSPENSAO_INICIAL,
  type EstadoSuspensao,
  type ForcaPerturbacao,
} from '../../dominio/didatico/suspensaoVeiculo'
import { Rotulo3D } from '../componentes/Rotulo3D'
import { Vetor3D } from '../componentes/Vetor3D'
import { useMaquete } from '../componentes/contextoMaquete'
import { Peca } from '../componentes/Peca'
import { SuspensaoVeicular } from '../veiculo/SuspensaoVeicular'
import { VeiculoDidatico } from '../veiculo/VeiculoDidatico'
import { RodaRealista } from '../veiculo/RodaRealista'
import { VitimaProcedural } from '../vitima/VitimaProcedural'
import { BlocoCribbing3D, CunhaResgate3D, StepChock3D } from './EquipamentosCribbing'
import { VStrut3D } from './VStrut3D'
import { ponteiroArrastou, type PontoDeTela } from '../gestoPonteiro'

export interface PropsCenaEstabilizacao {
  montagem?: { pontosDisponiveis: readonly PontoMontagem[]; aoEscolherPonto: (id: string) => void; instrucao?: string; previa?: ColocacaoEquipamento }
  posicao: PosicaoEstabilizacao
  equipamentoSelecionado: EquipamentoEstabilizacao | null
  colocacoes: readonly ColocacaoEquipamento[]
  avaliacao: AvaliacaoEstabilizacao
  mostrarInstabilidade: boolean
  estadoSuspensao?: EstadoSuspensao
  colarCervical?: boolean
  aoPosicionar: (
    equipamento: EquipamentoEstabilizacao,
    posicao: PontoSolo,
    atributos?: Partial<ColocacaoEquipamento>,
  ) => void
  aoRemover: (id: string) => void
  aoAtualizarColocacao?: (id: string, atualizacao: Partial<ColocacaoEquipamento>) => void
  aoPerturbar?: (forca: ForcaPerturbacao) => void
  idSelecionado: string | null
  aoSelecionar: (id: string | null) => void
}

export function CenaEstabilizacao({
  montagem,
  posicao,
  equipamentoSelecionado,
  colocacoes: colocacoesRecebidas,
  avaliacao,
  mostrarInstabilidade,
  estadoSuspensao = ESTADO_SUSPENSAO_INICIAL,
  colarCervical = false,
  aoPosicionar,
  aoRemover,
  aoAtualizarColocacao,
  aoPerturbar,
  idSelecionado,
  aoSelecionar,
}: PropsCenaEstabilizacao) {
  // Escoras de sessões anteriores também voltam ao encaixe automático.
  const colocacoes = useMemo(() => colocacoesRecebidas.map(c => c.equipamento === 'vstrut' && c.ligacao ? { ...c, ligacao: undefined } : c), [colocacoesRecebidas])
  const { perfil } = useMaquete()
  const tombado = posicao === 'tombado'
  const capotado = posicao === 'capotado'
  const complexo = posicao === 'sobreOutroVeiculo'
  const posto = postoDoVeiculo(perfil, capotado ? 'invertido' : tombado ? 'lateral' : 'sobreRodas')
  const rotacaoZBase = complexo ? Math.PI : posto.rolagemRad
  const alturaBase = complexo ? perfil.tetoY * 1.7 : posto.alturaApoio

  const leituras = useMemo(
    () => new Map(avaliacao.leituras.map((leitura) => [leitura.colocacao.id, leitura])),
    [avaliacao.leituras],
  )
  const cobertasIds = useMemo(() => new Set(avaliacao.zonasCobertas), [avaliacao.zonasCobertas])

  const [arrastandoId, setArrastandoId] = useState<string | null>(null)
  const [girandoGizmoId, setGirandoGizmoId] = useState<string | null>(null)
  const arrastouRef = useRef(false)
  const pressaoRef = useRef<PontoDeTela | null>(null)

  // Registrado na janela, e não no plano do solo: o gesto pode começar sobre o
  // veículo, sobre um equipamento ou sobre o vazio e ainda assim terminar sobre
  // o solo, que é quando o clique dispararia.
  useEffect(() => {
    const registrarPressao = (evento: PointerEvent) => {
      pressaoRef.current = { x: evento.clientX, y: evento.clientY }
    }
    window.addEventListener('pointerdown', registrarPressao)
    return () => window.removeEventListener('pointerdown', registrarPressao)
  }, [])

  /** O ponteiro andou o bastante para isto ser órbita de câmera, não clique. */
  const gestoFoiArrasto = (evento: { nativeEvent: MouseEvent }) =>
    ponteiroArrastou(pressaoRef.current, { x: evento.nativeEvent.clientX, y: evento.nativeEvent.clientY })

  let camera: THREE.Camera | undefined
  let raycaster: THREE.Raycaster | undefined
  let estadoThree: RootState | undefined
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const three = useThree()
    estadoThree = three
    camera = three.camera
    raycaster = three.raycaster
  } catch {
    // SSR / testes fora do Canvas
  }
  const chaoPlano = useMemo(() => new THREE.Plane(new THREE.Vector3(0, 1, 0), 0), [])

  // Os pontos são desenhados sobre a carroceria: priorize também seu hit,
  // para que a seleção de peças do carro não capture a montagem por baixo.
  const escolhendoPonto = !!montagem?.pontosDisponiveis.length
  useEffect(() => {
    if (!estadoThree || !escolhendoPonto) return
    const anterior = estadoThree.events.filter
    const ehMarcador = (objeto: THREE.Object3D | null): boolean => !!objeto && (objeto.name.startsWith('ponto-montagem-') || ehMarcador(objeto.parent))
    estadoThree.setEvents({ filter: (hits, state) => {
      const filtrados = anterior ? anterior(hits, state) : hits
      return [...filtrados].sort((a, b) => Number(ehMarcador(b.object)) - Number(ehMarcador(a.object)))
    } })
    return () => estadoThree.setEvents({ filter: anterior })
  }, [estadoThree?.setEvents, escolhendoPonto])

  useEffect(() => {
    if (!estadoThree?.controls || (!arrastandoId && !girandoGizmoId)) return
    const controls = estadoThree.controls as unknown as { enabled: boolean }
    const antes = controls.enabled
    controls.enabled = false
    return () => { controls.enabled = antes }
  }, [arrastandoId, girandoGizmoId, estadoThree?.controls])

  // Atalhos de teclado: Escape (desmarcar), Delete/Backspace (excluir), R (girar 90°), I (inverter calço)
  useEffect(() => {
    const tratarTecla = (e: KeyboardEvent) => {
      if (['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes((e.target as HTMLElement)?.tagName)) return

      if (e.key === 'Escape') {
        aoSelecionar(null)
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && idSelecionado) {
        aoRemover(idSelecionado)
        aoSelecionar(null)
      } else if ((e.key === 'r' || e.key === 'R') && idSelecionado) {
        const coloc = colocacoes.find((c) => c.id === idSelecionado)
        if (coloc && !coloc.ligacao) {
          const novaRot = Math.round(((coloc.rotacaoGraus ?? 0) + 90) % 360)
          aoAtualizarColocacao?.(idSelecionado, { rotacaoGraus: novaRot })
        }
      } else if ((e.key === 'i' || e.key === 'I') && idSelecionado) {
        const coloc = colocacoes.find((c) => c.id === idSelecionado)
        if (coloc && coloc.equipamento === 'calco') {
          aoAtualizarColocacao?.(idSelecionado, { invertido: !coloc.invertido })
        }
      }
    }
    window.addEventListener('keydown', tratarTecla)
    return () => window.removeEventListener('keydown', tratarTecla)
  }, [aoSelecionar, aoRemover, aoAtualizarColocacao, idSelecionado, colocacoes])

  // Rastreamento suave de arrasto e rotação na janela toda (não perde foco ao passar sobre o carro)
  useEffect(() => {
    if (!arrastandoId && !girandoGizmoId) return
    if (!camera || !raycaster) return

    const pontoSoloTemp = new THREE.Vector3()

    const onPointerMove = (e: PointerEvent) => {
      const canvas = window.document.querySelector('canvas')
      if (!canvas) return
      const rect = canvas.getBoundingClientRect()
      const ndcX = ((e.clientX - rect.left) / rect.width) * 2 - 1
      const ndcY = -((e.clientY - rect.top) / rect.height) * 2 + 1

      raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera)
      if (!raycaster.ray.intersectPlane(chaoPlano, pontoSoloTemp)) return

      arrastouRef.current = true

      if (girandoGizmoId) {
        const coloc = colocacoes.find((c) => c.id === girandoGizmoId)
        if (coloc) {
          const dx = pontoSoloTemp.x - coloc.posicao.x
          const dz = pontoSoloTemp.z - coloc.posicao.z
          const graus = Math.round(((-Math.atan2(dz, dx) * 180) / Math.PI + 360) % 360)
          aoAtualizarColocacao?.(girandoGizmoId, { rotacaoGraus: graus })
        }
        return
      }

      if (arrastandoId) {
        const coloc = colocacoes.find((c) => c.id === arrastandoId)
        if (coloc) {
          const outrasCobertas = new Set(
            colocacoes
              .filter((c) => c.id !== arrastandoId)
              .map((c) => leituras.get(c.id)?.zona?.id)
              .filter(Boolean) as string[],
          )
          const zonaSnap = coloc.ligacao ? null : encontrarZonaDeSnap(
            posicao,
            perfil,
            coloc.equipamento,
            { x: pontoSoloTemp.x, z: pontoSoloTemp.z },
            outrasCobertas,
          )
          if (zonaSnap) {
            aoAtualizarColocacao?.(arrastandoId, {
              ...(coloc.ligacao ? { ligacao: { ...coloc.ligacao, tensionada: false } } : {}),
              posicao: { x: zonaSnap.centro.x, z: zonaSnap.centro.z },
              rotacaoGraus: zonaSnap.rotacaoPadraoGraus ?? coloc.rotacaoGraus,
              invertido: zonaSnap.invertidoPadrao ?? coloc.invertido,
            })
          } else {
            aoAtualizarColocacao?.(arrastandoId, {
              ...(coloc.ligacao ? { ligacao: { ...coloc.ligacao, tensionada: false } } : {}),
              posicao: {
                x: Number(pontoSoloTemp.x.toFixed(2)),
                z: Number(pontoSoloTemp.z.toFixed(2)),
              },
            })
          }
        }
      }
    }

    const onPointerUp = () => {
      setArrastandoId(null)
      setGirandoGizmoId(null)
    }

    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)
    return () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerUp)
    }
  }, [
    arrastandoId,
    girandoGizmoId,
    camera,
    raycaster,
    chaoPlano,
    colocacoes,
    posicao,
    perfil,
    leituras,
    aoAtualizarColocacao,
  ])

  const posicionar = (evento: ThreeEvent<MouseEvent>) => {
    evento.stopPropagation()
    if (gestoFoiArrasto(evento)) return
    if (arrastouRef.current) {
      arrastouRef.current = false
      return
    }
    if (equipamentoSelecionado === 'fitaCatraca' && montagem) return
    // Se havia um objeto selecionado, clicar no solo vazio desseleciona e fecha o menu
    if (idSelecionado) {
      aoSelecionar(null)
      return
    }

    if (!equipamentoSelecionado) return
    const zonaSnap = encontrarZonaDeSnap(
      posicao,
      perfil,
      equipamentoSelecionado,
      { x: evento.point.x, z: evento.point.z },
      cobertasIds,
    )

    if (zonaSnap) {
      aoPosicionar(
        equipamentoSelecionado,
        {
          x: zonaSnap.centro.x,
          z: zonaSnap.centro.z,
        },
        {
          rotacaoGraus: zonaSnap.rotacaoPadraoGraus ?? 0,
          invertido: zonaSnap.invertidoPadrao ?? false,
          composicaoBloco: zonaSnap.dimensoesVazio ? zonaSnap.dimensoesVazio[1] > 0.2 : false,
        },
      )
    } else {
      aoPosicionar(equipamentoSelecionado, {
        x: Number(evento.point.x.toFixed(2)),
        z: Number(evento.point.z.toFixed(2)),
      })
    }
  }

  const posicionarNaZona = (zonaSnap: ZonaEstabilizacao) => {
    aoPosicionar(
      zonaSnap.equipamento,
      {
        x: zonaSnap.centro.x,
        z: zonaSnap.centro.z,
      },
      {
        rotacaoGraus: zonaSnap.rotacaoPadraoGraus ?? 0,
        invertido: zonaSnap.invertidoPadrao ?? false,
        composicaoBloco: zonaSnap.dimensoesVazio ? zonaSnap.dimensoesVazio[1] > 0.2 : false,
      },
    )
  }

  // Interação de balançar/empurrar o carro diretamente pelo clique/arraste
  const empurrarCarro = (evento: ThreeEvent<MouseEvent>) => {
    // Deixa o raio alcançar os marcadores, inclusive no assoalho exposto.
    if (montagem?.instrucao) return
    if (idSelecionado) {
      aoSelecionar(null)
      return
    }
    evento.stopPropagation()
    const ponto = evento.point

    if (posicao === 'quatroRodas') {
      if (avaliacao.suspensaoAnulada && avaliacao.rolamentoTravado) return
      const torqueRoll = ponto.x < 0 ? -12 : 12
      const torquePitch = ponto.z < 0 ? 10 : -10
      aoPerturbar?.({
        forcaY: -14,
        torqueRoll: torqueRoll * (1 - (avaliacao.rigidezProgressiva?.roll ?? 0)),
        torquePitch: torquePitch * (1 - (avaliacao.rigidezProgressiva?.pitch ?? 0)),
        forcaZ: (ponto.z < 0 ? 4 : -4) * (1 - (avaliacao.rigidezProgressiva?.longitudinal ?? 0)),
      })
    } else if (posicao === 'tombado') {
      if (avaliacao.primariaConcluida) return
      // Tombado: instabilidade lateral / perigo de queda
      const torqueRoll = (ponto.x < 0 ? -16 : 16) * (1 - (avaliacao.rigidezProgressiva?.roll ?? 0))
      aoPerturbar?.({
        forcaY: -8,
        torqueRoll,
        torquePitch: (ponto.z < 0 ? 5 : -5) * (1 - (avaliacao.rigidezProgressiva?.pitch ?? 0)),
      })
    } else if (posicao === 'capotado') {
      if (avaliacao.primariaConcluida) return
      // Capotado: gangorra longitudinal entre dianteira e traseira
      const torquePitch = (ponto.z < 0 ? 16 : -16) * (1 - (avaliacao.rigidezProgressiva?.pitch ?? 0))
      aoPerturbar?.({
        forcaY: -8,
        torquePitch,
        torqueRoll: (ponto.x < 0 ? -6 : 6) * (1 - (avaliacao.rigidezProgressiva?.roll ?? 0)),
      })
    } else if (posicao === 'sobreOutroVeiculo') {
      if (avaliacao.secundariaConcluida) return
      aoPerturbar?.({
        forcaZ: (ponto.z < 0 ? 6 : -6) * (1 - (avaliacao.rigidezProgressiva?.pitch ?? 0)),
        torqueRoll: (ponto.x < 0 ? -10 : 10) * (1 - (avaliacao.rigidezProgressiva?.roll ?? 0)),
      })
    }
  }

  // Deslocamentos e rotações vindos da física de estabilização e perturbação progressiva
  const fatorRoll = 1 - (avaliacao.rigidezProgressiva?.roll ?? (avaliacao.suspensaoAnulada ? 1 : 0))
  const fatorPitch = 1 - (avaliacao.rigidezProgressiva?.pitch ?? (avaliacao.suspensaoAnulada ? 1 : 0))
  const fatorVert = 1 - (avaliacao.rigidezProgressiva?.vertical ?? (avaliacao.suspensaoAnulada ? 1 : 0))

  const posY = alturaBase + (posicao === 'quatroRodas'
    ? estadoSuspensao.deslocamentoY
    : estadoSuspensao.deslocamentoY * 0.4 * fatorVert)
  const posZ = posicao === 'quatroRodas'
    ? estadoSuspensao.deslocamentoZ
    : estadoSuspensao.deslocamentoZ * 0.4 * fatorPitch
  const rotX = posicao === 'quatroRodas'
    ? estadoSuspensao.anguloPitch
    : posto.inclinacaoLongitudinalRad + (estadoSuspensao.anguloPitch * 0.8 * fatorPitch)
  const rotZ = posicao === 'quatroRodas'
    ? rotacaoZBase + estadoSuspensao.anguloRoll
    : rotacaoZBase + (estadoSuspensao.anguloRoll * 0.8 * fatorRoll)

  const pontosLigacao = pontosMontagem(posicao, perfil, { y: posY, z: posZ, rx: rotX, rz: rotZ })

  // Pose da vítima reagindo inercialmente à oscilação da suspensão
  const ancoraMotorista = perfil.ancoras.find((a) => a.id === 'motorista') ?? perfil.ancoras[0]!
  const colocacaoVitima = useMemo(
    () => ({
      posicaoPelve: ancoraMotorista.posicao,
      guinadaRad: ancoraMotorista.rotacao.y,
    }),
    [ancoraMotorista],
  )
  const poseDinamica = useMemo(
    () => calcularPoseComSuspensao(POSE_SENTADA, estadoSuspensao, colarCervical),
    [estadoSuspensao, colarCervical],
  )
  const compressaoDianteira = -calcularDeslocamentoVerticalNoPonto(estadoSuspensao, {
    x: 0,
    z: perfil.eixoDianteiroZ,
  })
  const compressaoTraseira = -calcularDeslocamentoVerticalNoPonto(estadoSuspensao, {
    x: 0,
    z: perfil.eixoTraseiroZ,
  })

  return (
    <group>
      {complexo && <VeiculoBase perfil={perfil} />}

      {posicao === 'quatroRodas' ? (
        /* O rolamento longitudinal move todo o conjunto. As rodas permanecem
           apoiadas no solo; somente a massa suspensa recebe Y/roll/pitch. */
        <group position={[0, alturaBase, posZ]} onPointerDown={empurrarCarro}>
          <Peca id="rodas">
            <RodaRealista perfil={perfil} />
          </Peca>
          <SuspensaoVeicular
            perfil={perfil}
            compressaoDianteira={compressaoDianteira}
            compressaoTraseira={compressaoTraseira}
            destaque={!avaliacao.suspensaoAnulada}
          />
          <group
            position={[0, estadoSuspensao.deslocamentoY, 0]}
            rotation={[rotX, 0, estadoSuspensao.anguloRoll]}
          >
            <VeiculoDidatico
              perfil={perfil}
              mostrarRodas={false}
              mostrarSuspensao={false}
              destaqueSuspensao={!avaliacao.suspensaoAnulada}
            />
            <VitimaProcedural
              colocacao={colocacaoVitima}
              pose={poseDinamica}
              colarCervical={colarCervical}
              proporcoes={ADULTO_MASCULINO}
            />
          </group>
        </group>
      ) : (
        /* Tombado, capotado e cenário complexo reagem rigidamente às forças. */
        <group
          position={[0, posY, posZ]}
          rotation={[rotX, 0, rotZ]}
          onPointerDown={empurrarCarro}
        >
          <VeiculoDidatico perfil={perfil} destaqueSuspensao={false} />
        </group>
      )}

      {/* Destaque holográfico fiel dos vazios e pontos compatíveis com o equipamento selecionado */}
      {equipamentoSelecionado && !(montagem && equipamentoSelecionado === 'fitaCatraca') && (
        <DestaquesVazios
          posicao={posicao}
          perfil={perfil}
          equipamentoSelecionado={equipamentoSelecionado}
          zonasCobertas={cobertasIds}
          aoPosicionarNaZona={posicionarNaZona}
        />
      )}

      {/* Equipamentos de resgate colocados na cena */}
      {colocacoes.map((colocacao) => (
        <EquipamentoNaCena
          key={colocacao.id}
          colocacao={colocacao}
          leitura={leituras.get(colocacao.id)}
          estado={leituras.get(colocacao.id)?.estado ?? 'fora'}
          posicaoVeiculo={posicao}
          perfil={perfil}
          alturaVeiculo={alturaBase}
          pontosLigacao={pontosLigacao}
          selecionado={idSelecionado === colocacao.id}
          gestoFoiArrasto={gestoFoiArrasto}
          aoSelecionar={() => aoSelecionar(colocacao.id === idSelecionado ? null : colocacao.id)}
          aoIniciarArrasto={() => setArrastandoId(colocacao.id)}
          aoIniciarGiroGizmo={() => setGirandoGizmoId(colocacao.id)}
        />
      ))}

      {montagem?.previa?.ligacao && (() => {
        const a = pontosLigacao.find(p => p.id === montagem.previa!.ligacao!.origem)
        const b = pontosLigacao.find(p => p.id === montagem.previa!.ligacao!.destino)
        return a && b ? <group name="previa-catraca"><LigacaoCatraca3D origem={a.ponto} destino={b.ponto} retorno={montagem.previa!.ligacao!.retorno} /></group> : null
      })()}
      {((equipamentoSelecionado === 'fitaCatraca' && montagem?.instrucao) || colocacoes.some(c => [c.ligacao?.origem, c.ligacao?.destino].some(id => id === 'poste' || id === 'arvore'))) && <ApoiosExternosMontagem />}
      {montagem?.pontosDisponiveis.map(p => {
        const vivo = pontosLigacao.find(q => q.id === p.id) ?? p
        return <group key={p.id} position={vivo.ponto} name={`ponto-montagem-${p.id}`}
          onPointerDown={e => e.stopPropagation()} onClick={e => { e.stopPropagation(); if (!gestoFoiArrasto(e)) montagem.aoEscolherPonto(p.id) }}>
          <mesh><sphereGeometry args={[0.095, 12, 12]} /><meshBasicMaterial color={p.uso === 'contato' ? '#38bdf8' : '#facc15'} transparent opacity={0.9} depthTest={false} /></mesh>
          <Rotulo3D posicao={[0, 0.16, 0]} texto={p.nome} variante="neutro" fator={4} sempreVisivel />
        </group>
      })}
      {montagem?.instrucao && equipamentoSelecionado === 'fitaCatraca' && <Rotulo3D posicao={[0, alturaBase + perfil.altura + 0.3, 0]} texto={montagem.instrucao} variante="neutro" fator={5} />}

      {mostrarInstabilidade && (
        <PontosInstabilidade posicao={posicao} perfil={perfil} />
      )}

      {/* Plano invisível de detecção de clique e arrasto no solo */}
      <mesh
        position={[0, 0.012, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        onPointerMove={(e) => {
          if (girandoGizmoId) {
            e.stopPropagation()
            arrastouRef.current = true
            const coloc = colocacoes.find((c) => c.id === girandoGizmoId)
            if (coloc) {
              const dx = e.point.x - coloc.posicao.x
              const dz = e.point.z - coloc.posicao.z
              const graus = Math.round(((-Math.atan2(dz, dx) * 180) / Math.PI + 360) % 360)
              aoAtualizarColocacao?.(girandoGizmoId, { rotacaoGraus: graus })
            }
            return
          }
          if (arrastandoId) {
            e.stopPropagation()
            arrastouRef.current = true
            const coloc = colocacoes.find((c) => c.id === arrastandoId)
            if (coloc) {
              const outrasCobertas = new Set(
                colocacoes
                  .filter((c) => c.id !== arrastandoId)
                  .map((c) => leituras.get(c.id)?.zona?.id)
                  .filter(Boolean) as string[],
              )
              const zonaSnap = coloc.ligacao ? null : encontrarZonaDeSnap(
                posicao,
                perfil,
                coloc.equipamento,
                { x: e.point.x, z: e.point.z },
                outrasCobertas,
              )
              if (zonaSnap) {
                aoAtualizarColocacao?.(arrastandoId, {
                  ...(coloc.ligacao ? { ligacao: { ...coloc.ligacao, tensionada: false } } : {}),
                  posicao: { x: zonaSnap.centro.x, z: zonaSnap.centro.z },
                  rotacaoGraus: zonaSnap.rotacaoPadraoGraus ?? coloc.rotacaoGraus,
                  invertido: zonaSnap.invertidoPadrao ?? coloc.invertido,
                })
              } else {
                aoAtualizarColocacao?.(arrastandoId, {
                  ...(coloc.ligacao ? { ligacao: { ...coloc.ligacao, tensionada: false } } : {}),
                  posicao: {
                    x: Number(e.point.x.toFixed(2)),
                    z: Number(e.point.z.toFixed(2)),
                  },
                })
              }
            }
          }
        }}
        onPointerUp={() => {
          setArrastandoId(null)
          setGirandoGizmoId(null)
        }}
        onClick={posicionar}
      >
        <planeGeometry args={[20, 20]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>

      <Rotulo3D
        posicao={[0, Math.max(perfil.tetoY, alturaBase + perfil.altura) + 0.65, 0]}
        texto={
          montagem?.instrucao ?? (idSelecionado
            ? 'equipamento selecionado: use o inspetor lateral, arraste para mover, ou clique fora / Esc para sair'
            : !equipamentoSelecionado
              ? 'modo seleção: clique em um apoio para inspecionar, ou escolha uma ferramenta na barra lateral'
              : !avaliacao.suspensaoAnulada && posicao === 'quatroRodas'
                ? 'clique no veículo para testar o balanço, ou aproxime do pneu/estrutura para encaixar'
                : 'clique no veículo para testar estabilidade, ou aproxime da área destacada para encaixar')
        }
        variante="neutro"
        sempreVisivel={!!montagem?.instrucao}
        fator={6}
      />
    </group>
  )
}

function PontosInstabilidade({ posicao, perfil }: { posicao: PosicaoEstabilizacao; perfil: PerfilVeiculo }) {
  return (
    <>
      {vetoresDeInstabilidade(posicao, perfil).map((vetor) => (
        <group key={vetor.id}>
          <Vetor3D
            origem={{ x: vetor.origem[0], y: vetor.origem[1], z: vetor.origem[2] }}
            destino={{ x: vetor.destino[0], y: vetor.destino[1], z: vetor.destino[2] }}
            cor="#ff4f64"
            espessura={7}
          />
          <Rotulo3D posicao={vetor.destino} texto={vetor.leitura} variante="risco" fator={5} />
        </group>
      ))}
    </>
  )
}

function DestaquesVazios({
  posicao,
  perfil,
  equipamentoSelecionado,
  zonasCobertas,
  aoPosicionarNaZona,
}: {
  posicao: PosicaoEstabilizacao
  perfil: PerfilVeiculo
  equipamentoSelecionado: EquipamentoEstabilizacao | null
  zonasCobertas: ReadonlySet<string>
  aoPosicionarNaZona: (zona: ZonaEstabilizacao) => void
}) {
  const zonasCompativeis = useMemo(() => {
    if (!equipamentoSelecionado) return []
    const compativeis = zonasDaEstabilizacao(posicao, perfil).filter((zona) => {
      if (zonasCobertas.has(zona.id)) return false
      return equipamentoCompativelComZona(zona, equipamentoSelecionado, posicao)
    })
    return semVizinhasDeOutroEquipamento(compativeis, equipamentoSelecionado)
  }, [posicao, perfil, equipamentoSelecionado, zonasCobertas])

  if (!equipamentoSelecionado || zonasCompativeis.length === 0) return null

  return (
    <group name="destaques-vazios">
      {zonasCompativeis.map((zona) => (
        <SlotHolografico
          key={zona.id}
          zona={zona}
          equipamento={equipamentoSelecionado}
          aoClicar={aoPosicionarNaZona}
        />
      ))}
    </group>
  )
}

/** Abaixo desta distância no solo, dois hologramas se sobrepõem na leitura. */
const DISTANCIA_SOBREPOSICAO_HOLOGRAMA = 0.45

/**
 * Esconde a zona que só aceita o equipamento selecionado por tabela, quando há
 * ao lado uma zona feita para ele. No meio do veículo, o apoio da coluna B
 * (calço) e o bloco de nivelamento ficam a poucos centímetros: mostrar os dois
 * desenhava dois equipamentos no mesmo lugar. A zona continua valendo na
 * avaliação; só o holograma sai.
 */
export function semVizinhasDeOutroEquipamento(
  zonas: readonly ZonaEstabilizacao[],
  equipamento: EquipamentoEstabilizacao,
): ZonaEstabilizacao[] {
  return zonas.filter((zona) => {
    if (zona.equipamento === equipamento) return true
    return !zonas.some((outra) =>
      outra !== zona
      && outra.equipamento === equipamento
      && Math.hypot(outra.centro.x - zona.centro.x, outra.centro.z - zona.centro.z) < DISTANCIA_SOBREPOSICAO_HOLOGRAMA)
  })
}

function SlotHolografico({
  zona: zonaOriginal,
  equipamento,
  aoClicar,
}: {
  zona: ZonaEstabilizacao
  /** o holograma mostra o equipamento que está na mão, não o padrão da zona */
  equipamento: EquipamentoEstabilizacao
  aoClicar: (zona: ZonaEstabilizacao) => void
}) {
  const zona = zonaOriginal.tipo === 'escora' ? zonaOriginal : { ...zonaOriginal, equipamento }
  const [hovered, setHovered] = useState(false)
  const pontoSolo = zona.pontoSolo ?? [zona.centro.x, 0, zona.centro.z]
  const pontoVeiculo = zona.pontoVeiculo
  const rotY = ((zona.rotacaoPadraoGraus ?? 0) * Math.PI) / 180

  const handleClick = (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation()
    // coloca o que o holograma mostra: o equipamento que está na mão
    aoClicar(zona)
  }

  const handlePointerOver = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation()
    setHovered(true)
  }

  const handlePointerOut = () => {
    setHovered(false)
  }

  if (zona.tipo === 'escora' && pontoVeiculo) {
    return (
      <group
        key={zona.id}
        onClick={handleClick}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
      >
        {/* Pé da escora no solo */}
        <mesh position={[pontoSolo[0], 0.015, pontoSolo[2]]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.15, 0.28, 32]} />
          <meshBasicMaterial color={hovered ? '#38bdf8' : '#0284c7'} transparent opacity={hovered ? 0.85 : 0.65} />
        </mesh>
        {/* Cabeça da escora no ponto estrutural */}
        <mesh position={pontoVeiculo}>
          <sphereGeometry args={[0.08, 16, 16]} />
          <meshStandardMaterial color="#0284c7" emissive="#38bdf8" emissiveIntensity={hovered ? 1.0 : 0.7} />
        </mesh>
        {/* Linha guia projetada */}
        <Line
          points={[
            [pontoSolo[0], 0.02, pontoSolo[2]],
            pontoVeiculo,
          ]}
          color={hovered ? '#38bdf8' : '#0284c7'}
          lineWidth={hovered ? 5 : 3}
          dashed
          dashScale={12}
          dashSize={0.15}
          gapSize={0.08}
        />
        <Rotulo3D
          posicao={[pontoSolo[0], 0.35, pontoSolo[2]]}
          texto={`Ponto V-Strut: ${zona.nome}`}
          variante="zona"
          fator={4}
        />
      </group>
    )
  }

  // Holograma fiel do Calço Escalonado
  if (zona.equipamento === 'calco') {
    return (
      <group
        key={zona.id}
        position={[pontoSolo[0], 0, pontoSolo[2]]}
        onClick={handleClick}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
      >
        <StepChock3D
          fantasma
          emDestaque={hovered}
          rotacaoY={rotY}
          invertido={zona.invertidoPadrao}
        />
        <mesh position={[0, 0.005, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.2, 0.28, 32]} />
          <meshBasicMaterial
            color={hovered ? '#38bdf8' : '#0284c7'}
            transparent
            opacity={hovered ? 0.75 : 0.35}
          />
        </mesh>
        <Rotulo3D
          posicao={[0, 0.36, 0]}
          texto={zona.nome}
          variante="zona"
          fator={4}
        />
      </group>
    )
  }

  // Holograma fiel da Cunha de Resgate (bloqueio de roda ou ajuste)
  if (zona.equipamento === 'cunha') {
    return (
      <group
        key={zona.id}
        position={[pontoSolo[0], 0, pontoSolo[2]]}
        onClick={handleClick}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
      >
        <CunhaResgate3D
          fantasma
          emDestaque={hovered}
          rotacaoY={rotY}
        />
        <mesh position={[0, 0.005, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.12, 0.18, 24]} />
          <meshBasicMaterial
            color={hovered ? '#34d399' : '#059669'}
            transparent
            opacity={hovered ? 0.75 : 0.35}
          />
        </mesh>
        <Rotulo3D
          posicao={[0, 0.22, 0]}
          texto={zona.nome}
          variante="zona"
          fator={3.5}
        />
      </group>
    )
  }

  // Holograma fiel do Bloco de Cribbing Modular
  if (zona.equipamento === 'bloco') {
    const camadas = zona.dimensoesVazio ? Math.max(1, Math.round(zona.dimensoesVazio[1] / 0.12)) : 1
    const altTotal = camadas * 0.12
    return (
      <group
        key={zona.id}
        position={[pontoSolo[0], 0, pontoSolo[2]]}
        onClick={handleClick}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
      >
        <BlocoCribbing3D
          fantasma
          emDestaque={hovered}
          camadas={camadas}
          rotacaoY={rotY}
          comCunhaTopo={zona.id.startsWith('capo-')}
        />
        <mesh position={[0, 0.005, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.22, 0.28, 32]} />
          <meshBasicMaterial
            color={hovered ? '#fbbf24' : '#d97706'}
            transparent
            opacity={hovered ? 0.75 : 0.35}
          />
        </mesh>
        <Rotulo3D
          posicao={[0, altTotal + 0.18, 0]}
          texto={zona.nome}
          variante="zona"
          fator={4}
        />
      </group>
    )
  }

  // Fallback genérico
  return (
    <group
      key={zona.id}
      position={[pontoSolo[0], 0.015, pontoSolo[2]]}
      onClick={handleClick}
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
    >
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[zona.raio * 0.4, zona.raio * 0.7, 32]} />
        <meshBasicMaterial color={hovered ? '#38bdf8' : '#fbbf24'} transparent opacity={hovered ? 0.8 : 0.5} />
      </mesh>
    </group>
  )
}

function EquipamentoNaCena({
  colocacao,
  leitura,
  estado,
  posicaoVeiculo,
  perfil,
  alturaVeiculo,
  pontosLigacao,
  selecionado,
  gestoFoiArrasto,
  aoSelecionar,
  aoIniciarArrasto,
  aoIniciarGiroGizmo,
}: {
  colocacao: ColocacaoEquipamento
  leitura?: any
  estado: 'correta' | 'proxima' | 'fora' | 'naoPrevista'
  posicaoVeiculo: PosicaoEstabilizacao
  perfil: PerfilVeiculo
  alturaVeiculo: number
  pontosLigacao: readonly PontoMontagem[]
  selecionado: boolean
  /** Mesma regra do solo: órbita de câmera não seleciona equipamento. */
  gestoFoiArrasto: (evento: { nativeEvent: MouseEvent }) => boolean
  aoSelecionar: () => void
  aoIniciarArrasto: () => void
  aoIniciarGiroGizmo: () => void
}) {
  const cor = estado === 'correta' ? '#10b981' : estado === 'proxima' ? '#f59e0b' : '#ef4444'
  const base: [number, number, number] = [colocacao.posicao.x, 0, colocacao.posicao.z]

  const grausAtual = Math.round(((colocacao.rotacaoGraus ?? 0) % 360 + 360) % 360)
  const rotacaoFinal = (grausAtual * Math.PI) / 180
  const aoClicar = (evento: ThreeEvent<MouseEvent>) => {
    evento.stopPropagation()
    if (gestoFoiArrasto(evento)) return
    aoSelecionar()
  }

  const aoAbaixar = (evento: ThreeEvent<MouseEvent>) => {
    evento.stopPropagation()
    if (colocacao.equipamento !== 'fitaCatraca') aoIniciarArrasto()
  }

  return (
    <group position={base} onPointerDown={aoAbaixar} onClick={aoClicar}>
      {/* Indicador de seleção no solo e anel/gizmo de rotação no próprio eixo */}
      {selecionado && !colocacao.ligacao && (
        <group name="selecao-equipamento" position={[0, 0.005, 0]}>
          {/* Anel circular de referência no solo */}
          <mesh rotation={[-Math.PI / 2, 0, 0]}>
            <ringGeometry args={[0.38, 0.44, 48]} />
            <meshBasicMaterial color="#38bdf8" transparent opacity={0.65} />
          </mesh>

          {/* Seta e alça interativa 3D indicando a orientação atual */}
          <group rotation={[0, rotacaoFinal, 0]}>
            <mesh position={[0.42, 0.008, 0]} rotation={[0, 0, -Math.PI / 2]}>
              <coneGeometry args={[0.045, 0.1, 16]} />
              <meshBasicMaterial color="#38bdf8" />
            </mesh>

            <mesh
              position={[0.52, 0.02, 0]}
              onPointerDown={(e) => {
                e.stopPropagation()
                aoIniciarGiroGizmo()
              }}
            >
              <sphereGeometry args={[0.05, 16, 16]} />
              <meshStandardMaterial color="#f59e0b" emissive="#d97706" emissiveIntensity={0.65} />
            </mesh>
          </group>
        </group>
      )}

      {/* Modelagem de acordo com o equipamento */}
      {colocacao.equipamento === 'calco' && (
        <StepChock3D
          cor={cor}
          rotacaoY={rotacaoFinal}
          comCunhaAjuste={colocacao.comCunhaAjuste}
          invertido={colocacao.invertido}
        />
      )}

      {colocacao.equipamento === 'cunha' && (
        <CunhaResgate3D cor={cor} rotacaoY={rotacaoFinal} />
      )}

      {colocacao.equipamento === 'bloco' && (
        <group rotation={[0, rotacaoFinal, 0]}>
          <BlocoCribbing3D
            cor={cor}
            camadas={
              leitura?.zona?.dimensoesVazio
                ? Math.max(1, Math.round(leitura.zona.dimensoesVazio[1] / 0.12))
                : colocacao.composicaoBloco
                  ? 2
                  : 1
            }
            comCunhaTopo={colocacao.composicaoBloco || colocacao.comCunhaAjuste}
          />
        </group>
      )}

      {colocacao.equipamento === 'vstrut' && (() => {
        const destinoMundo = leitura?.zona?.pontoVeiculo ?? contatoDoVeiculo(colocacao.posicao, posicaoVeiculo, perfil, alturaVeiculo)
        const paraLocal: [number, number, number] = [
          destinoMundo[0] - colocacao.posicao.x,
          destinoMundo[1],
          destinoMundo[2] - colocacao.posicao.z,
        ]

        // Ponto de ancoragem da cinta no veículo: prende na lata estrutural do monobloco.
        // Nunca ancorar na roda (a roda movimenta e desestabiliza o veículo).
        // A fita e o gancho do V-Strut ficam alinhados com a escora, ancorando na lata
        // próximo à cabeça / vidro da coluna C.
        const ancoraMundo: [number, number, number] = posicaoVeiculo === 'capotado'
          ? [
              destinoMundo[0] * 0.96,
              Math.max(0.12, destinoMundo[1] - 0.12),
              destinoMundo[2] + 0.03,
            ]
          : posicaoVeiculo === 'tombado'
            ? [
                destinoMundo[0] - 0.04,
                Math.max(0.35, destinoMundo[1] - 0.14),
                destinoMundo[2],
              ]
            : [destinoMundo[0], 0.14, destinoMundo[2]]

        const ancoraLocal: [number, number, number] = [
          ancoraMundo[0] - colocacao.posicao.x,
          ancoraMundo[1],
          ancoraMundo[2] - colocacao.posicao.z,
        ]

        return (
          <group name="montagem-vstrut">
          <VStrut3D
            para={paraLocal}
            pontoAncoragem={ancoraLocal}
            cor={cor}
            selecionado={selecionado}
          />
          </group>
        )
      })()}

      {colocacao.equipamento === 'fitaCatraca' && (() => {
        if (colocacao.ligacao) {
          const a = pontosLigacao.find(p => p.id === colocacao.ligacao!.origem)
          const b = pontosLigacao.find(p => p.id === colocacao.ligacao!.destino)
          if (!a || !b) return null
          const local = (p: PontoMontagem): [number, number, number] => [p.ponto[0] - base[0], p.ponto[1], p.ponto[2] - base[2]]
          return <LigacaoCatraca3D origem={local(a)} destino={local(b)} retorno={colocacao.ligacao.retorno} tensionada={colocacao.ligacao.tensionada} />
        }
        const destinoMundo = leitura?.zona?.pontoVeiculo ?? contatoDoVeiculo(colocacao.posicao, posicaoVeiculo, perfil, alturaVeiculo)
        const paraLocal: [number, number, number] = [
          destinoMundo[0] - colocacao.posicao.x,
          destinoMundo[1],
          destinoMundo[2] - colocacao.posicao.z,
        ]
        return (
          <group>
            <Line points={[[0, 0.08, 0], paraLocal]} color={cor} lineWidth={6} />
            <mesh position={[0, 0.1, 0]}>
              <boxGeometry args={[0.28, 0.18, 0.2]} />
              <meshStandardMaterial color={cor} metalness={0.35} />
            </mesh>
          </group>
        )
      })()}

    </group>
  )
}

function contatoDoVeiculo(
  base: PontoSolo,
  posicao: PosicaoEstabilizacao,
  perfil: PerfilVeiculo,
  alturaVeiculo: number,
): [number, number, number] {
  const sinal = base.x < 0 ? -1 : 1
  // tombado sobre o motorista: teto em +X, assoalho voltado para −X, lata da coluna C em ~0.72
  const x = posicao === 'tombado'
    ? (base.x < 0.7 ? 0.72 : perfil.tetoY - 0.1)
    : sinal * (perfil.largura / 2)
  const y = posicao === 'sobreOutroVeiculo'
    ? alturaVeiculo + 0.6
    : posicao === 'capotado'
      ? (() => {
          const posto = postoDoVeiculo(perfil, 'invertido')
          const theta = posto.inclinacaoLongitudinalRad
          const zLimitado = Math.max(perfil.traseiraZ, Math.min(perfil.frenteZ, base.z))
          const yLocal = Number((perfil.cinturaY - 0.05).toFixed(2))
          return Number((posto.alturaApoio - yLocal * Math.cos(theta) - zLimitado * Math.sin(theta)).toFixed(2))
        })()
      : posicao === 'tombado'
        ? (base.x < 0.7 ? Math.min(1.48, perfil.largura - 0.24) : 0.22)
        : perfil.cinturaY
  const z = Math.max(perfil.traseiraZ + 0.5, Math.min(perfil.frenteZ - 0.5, base.z))
  return [x, y, z]
}


function VeiculoBase({ perfil }: { perfil: PerfilVeiculo }) {
  return (
    <group>
      <mesh position={[0, perfil.cinturaY / 2, 0]}>
        <boxGeometry args={[perfil.largura, perfil.cinturaY, perfil.comprimento]} />
        <meshStandardMaterial color="#596875" roughness={0.75} />
      </mesh>
      <mesh position={[0, perfil.tetoY * 0.78, -0.2]}>
        <boxGeometry args={[perfil.largura * 0.84, perfil.tetoY * 0.5, perfil.comprimento * 0.5]} />
        <meshStandardMaterial color="#263c4d" roughness={0.55} />
      </mesh>
    </group>
  )
}


function ApoiosExternosMontagem() {
  return <group name="apoios-externos-montagem">
    <group position={[-3.3, 0, -0.8]} name="poste-montagem">
      <mesh position={[0, 1.15, 0]}><cylinderGeometry args={[0.11, 0.13, 2.3, 12]} /><meshStandardMaterial color="#8b949e" roughness={0.9} /></mesh>
      <Rotulo3D posicao={[0, 2.5, 0]} texto="Poste" variante="neutro" fator={4} />
    </group>
    <group position={[3.3, 0, 0.8]} name="arvore-montagem">
      <mesh position={[0, 1.05, 0]}><cylinderGeometry args={[0.12, 0.16, 2.1, 12]} /><meshStandardMaterial color="#715239" roughness={1} /></mesh>
      <mesh position={[0, 2.15, 0]}><sphereGeometry args={[0.7, 12, 10]} /><meshStandardMaterial color="#437253" roughness={1} /></mesh>
      <Rotulo3D posicao={[0, 3, 0]} texto="Árvore" variante="neutro" fator={4} />
    </group>
  </group>
}
